import z from "zod";

export const FiatCurrencySchema = z.object({
  symbol: z.string(),
  rate: z.number().nullable().optional(),
});

export type FiatCurrency = z.infer<typeof FiatCurrencySchema>;

export const FiatCurrencyMetadataSchema = z.object({
  symbol: z.string(),
  name: z.string(),
  symbolNative: z.string(),
});

export type FiatCurrencyMetadata = z.infer<typeof FiatCurrencyMetadataSchema>;

/**
 * Extended currency metadata with country mapping and flag emoji support
 */
export const CurrencyMetadataSchema = z.object({
  code: z.string(), // "usd" (lowercase for API compatibility)
  codeUpper: z.string(), // "USD" (uppercase for display)
  name: z.string(), // "US Dollar"
  symbol: z.string(), // "$"
  countryCode: z.string(), // "US" (ISO 3166-1 alpha-2)
  flagEmoji: z.string(), // "🇺🇸"
  decimalPlaces: z.number(), // 2 for USD, 0 for IDR
});

export type CurrencyMetadata = z.infer<typeof CurrencyMetadataSchema>;

/**
 * All supported currencies from CoinGecko API (47 fiat currencies)
 * Data sourced from:
 * - CoinGecko: https://docs.coingecko.com/reference/simple-supported-currencies
 * - ISO 4217 currency codes
 * - ISO 3166-1 alpha-2 country codes
 */
export const SUPPORTED_CURRENCIES: Record<string, CurrencyMetadata> = {
  usd: {
    code: "usd",
    codeUpper: "USD",
    name: "US Dollar",
    symbol: "$",
    countryCode: "US",
    flagEmoji: "🇺🇸",
    decimalPlaces: 2,
  },
  aed: {
    code: "aed",
    codeUpper: "AED",
    name: "UAE Dirham",
    symbol: "د.إ",
    countryCode: "AE",
    flagEmoji: "🇦🇪",
    decimalPlaces: 2,
  },
  ars: {
    code: "ars",
    codeUpper: "ARS",
    name: "Argentine Peso",
    symbol: "$",
    countryCode: "AR",
    flagEmoji: "🇦🇷",
    decimalPlaces: 2,
  },
  aud: {
    code: "aud",
    codeUpper: "AUD",
    name: "Australian Dollar",
    symbol: "A$",
    countryCode: "AU",
    flagEmoji: "🇦🇺",
    decimalPlaces: 2,
  },
  bdt: {
    code: "bdt",
    codeUpper: "BDT",
    name: "Bangladeshi Taka",
    symbol: "৳",
    countryCode: "BD",
    flagEmoji: "🇧🇩",
    decimalPlaces: 2,
  },
  bhd: {
    code: "bhd",
    codeUpper: "BHD",
    name: "Bahraini Dinar",
    symbol: ".د.ب",
    countryCode: "BH",
    flagEmoji: "🇧🇭",
    decimalPlaces: 3,
  },
  bmd: {
    code: "bmd",
    codeUpper: "BMD",
    name: "Bermudian Dollar",
    symbol: "$",
    countryCode: "BM",
    flagEmoji: "🇧🇲",
    decimalPlaces: 2,
  },
  brl: {
    code: "brl",
    codeUpper: "BRL",
    name: "Brazilian Real",
    symbol: "R$",
    countryCode: "BR",
    flagEmoji: "🇧🇷",
    decimalPlaces: 2,
  },
  cad: {
    code: "cad",
    codeUpper: "CAD",
    name: "Canadian Dollar",
    symbol: "C$",
    countryCode: "CA",
    flagEmoji: "🇨🇦",
    decimalPlaces: 2,
  },
  chf: {
    code: "chf",
    codeUpper: "CHF",
    name: "Swiss Franc",
    symbol: "CHF",
    countryCode: "CH",
    flagEmoji: "🇨🇭",
    decimalPlaces: 2,
  },
  clp: {
    code: "clp",
    codeUpper: "CLP",
    name: "Chilean Peso",
    symbol: "$",
    countryCode: "CL",
    flagEmoji: "🇨🇱",
    decimalPlaces: 0,
  },
  cny: {
    code: "cny",
    codeUpper: "CNY",
    name: "Chinese Yuan",
    symbol: "¥",
    countryCode: "CN",
    flagEmoji: "🇨🇳",
    decimalPlaces: 2,
  },
  czk: {
    code: "czk",
    codeUpper: "CZK",
    name: "Czech Koruna",
    symbol: "Kč",
    countryCode: "CZ",
    flagEmoji: "🇨🇿",
    decimalPlaces: 2,
  },
  dkk: {
    code: "dkk",
    codeUpper: "DKK",
    name: "Danish Krone",
    symbol: "kr",
    countryCode: "DK",
    flagEmoji: "🇩🇰",
    decimalPlaces: 2,
  },
  eur: {
    code: "eur",
    codeUpper: "EUR",
    name: "Euro",
    symbol: "€",
    countryCode: "EU",
    flagEmoji: "🇪🇺",
    decimalPlaces: 2,
  },
  gbp: {
    code: "gbp",
    codeUpper: "GBP",
    name: "British Pound",
    symbol: "£",
    countryCode: "GB",
    flagEmoji: "🇬🇧",
    decimalPlaces: 2,
  },
  gel: {
    code: "gel",
    codeUpper: "GEL",
    name: "Georgian Lari",
    symbol: "₾",
    countryCode: "GE",
    flagEmoji: "🇬🇪",
    decimalPlaces: 2,
  },
  hkd: {
    code: "hkd",
    codeUpper: "HKD",
    name: "Hong Kong Dollar",
    symbol: "HK$",
    countryCode: "HK",
    flagEmoji: "🇭🇰",
    decimalPlaces: 2,
  },
  huf: {
    code: "huf",
    codeUpper: "HUF",
    name: "Hungarian Forint",
    symbol: "Ft",
    countryCode: "HU",
    flagEmoji: "🇭🇺",
    decimalPlaces: 2,
  },
  idr: {
    code: "idr",
    codeUpper: "IDR",
    name: "Indonesian Rupiah",
    symbol: "Rp",
    countryCode: "ID",
    flagEmoji: "🇮🇩",
    decimalPlaces: 0,
  },
  ils: {
    code: "ils",
    codeUpper: "ILS",
    name: "Israeli New Shekel",
    symbol: "₪",
    countryCode: "IL",
    flagEmoji: "🇮🇱",
    decimalPlaces: 2,
  },
  inr: {
    code: "inr",
    codeUpper: "INR",
    name: "Indian Rupee",
    symbol: "₹",
    countryCode: "IN",
    flagEmoji: "🇮🇳",
    decimalPlaces: 2,
  },
  jpy: {
    code: "jpy",
    codeUpper: "JPY",
    name: "Japanese Yen",
    symbol: "¥",
    countryCode: "JP",
    flagEmoji: "🇯🇵",
    decimalPlaces: 0,
  },
  krw: {
    code: "krw",
    codeUpper: "KRW",
    name: "South Korean Won",
    symbol: "₩",
    countryCode: "KR",
    flagEmoji: "🇰🇷",
    decimalPlaces: 0,
  },
  kwd: {
    code: "kwd",
    codeUpper: "KWD",
    name: "Kuwaiti Dinar",
    symbol: "د.ك",
    countryCode: "KW",
    flagEmoji: "🇰🇼",
    decimalPlaces: 3,
  },
  lkr: {
    code: "lkr",
    codeUpper: "LKR",
    name: "Sri Lankan Rupee",
    symbol: "Rs",
    countryCode: "LK",
    flagEmoji: "🇱🇰",
    decimalPlaces: 2,
  },
  mmk: {
    code: "mmk",
    codeUpper: "MMK",
    name: "Myanmar Kyat",
    symbol: "K",
    countryCode: "MM",
    flagEmoji: "🇲🇲",
    decimalPlaces: 2,
  },
  mxn: {
    code: "mxn",
    codeUpper: "MXN",
    name: "Mexican Peso",
    symbol: "MX$",
    countryCode: "MX",
    flagEmoji: "🇲🇽",
    decimalPlaces: 2,
  },
  myr: {
    code: "myr",
    codeUpper: "MYR",
    name: "Malaysian Ringgit",
    symbol: "RM",
    countryCode: "MY",
    flagEmoji: "🇲🇾",
    decimalPlaces: 2,
  },
  ngn: {
    code: "ngn",
    codeUpper: "NGN",
    name: "Nigerian Naira",
    symbol: "₦",
    countryCode: "NG",
    flagEmoji: "🇳🇬",
    decimalPlaces: 2,
  },
  nok: {
    code: "nok",
    codeUpper: "NOK",
    name: "Norwegian Krone",
    symbol: "kr",
    countryCode: "NO",
    flagEmoji: "🇳🇴",
    decimalPlaces: 2,
  },
  nzd: {
    code: "nzd",
    codeUpper: "NZD",
    name: "New Zealand Dollar",
    symbol: "NZ$",
    countryCode: "NZ",
    flagEmoji: "🇳🇿",
    decimalPlaces: 2,
  },
  php: {
    code: "php",
    codeUpper: "PHP",
    name: "Philippine Peso",
    symbol: "₱",
    countryCode: "PH",
    flagEmoji: "🇵🇭",
    decimalPlaces: 2,
  },
  pkr: {
    code: "pkr",
    codeUpper: "PKR",
    name: "Pakistani Rupee",
    symbol: "Rs",
    countryCode: "PK",
    flagEmoji: "🇵🇰",
    decimalPlaces: 2,
  },
  pln: {
    code: "pln",
    codeUpper: "PLN",
    name: "Polish Złoty",
    symbol: "zł",
    countryCode: "PL",
    flagEmoji: "🇵🇱",
    decimalPlaces: 2,
  },
  rub: {
    code: "rub",
    codeUpper: "RUB",
    name: "Russian Ruble",
    symbol: "₽",
    countryCode: "RU",
    flagEmoji: "🇷🇺",
    decimalPlaces: 2,
  },
  sar: {
    code: "sar",
    codeUpper: "SAR",
    name: "Saudi Riyal",
    symbol: "﷼",
    countryCode: "SA",
    flagEmoji: "🇸🇦",
    decimalPlaces: 2,
  },
  sek: {
    code: "sek",
    codeUpper: "SEK",
    name: "Swedish Krona",
    symbol: "kr",
    countryCode: "SE",
    flagEmoji: "🇸🇪",
    decimalPlaces: 2,
  },
  sgd: {
    code: "sgd",
    codeUpper: "SGD",
    name: "Singapore Dollar",
    symbol: "S$",
    countryCode: "SG",
    flagEmoji: "🇸🇬",
    decimalPlaces: 2,
  },
  thb: {
    code: "thb",
    codeUpper: "THB",
    name: "Thai Baht",
    symbol: "฿",
    countryCode: "TH",
    flagEmoji: "🇹🇭",
    decimalPlaces: 2,
  },
  try: {
    code: "try",
    codeUpper: "TRY",
    name: "Turkish Lira",
    symbol: "₺",
    countryCode: "TR",
    flagEmoji: "🇹🇷",
    decimalPlaces: 2,
  },
  twd: {
    code: "twd",
    codeUpper: "TWD",
    name: "New Taiwan Dollar",
    symbol: "NT$",
    countryCode: "TW",
    flagEmoji: "🇹🇼",
    decimalPlaces: 2,
  },
  uah: {
    code: "uah",
    codeUpper: "UAH",
    name: "Ukrainian Hryvnia",
    symbol: "₴",
    countryCode: "UA",
    flagEmoji: "🇺🇦",
    decimalPlaces: 2,
  },
  vef: {
    code: "vef",
    codeUpper: "VEF",
    name: "Venezuelan Bolívar",
    symbol: "Bs",
    countryCode: "VE",
    flagEmoji: "🇻🇪",
    decimalPlaces: 2,
  },
  vnd: {
    code: "vnd",
    codeUpper: "VND",
    name: "Vietnamese Đồng",
    symbol: "₫",
    countryCode: "VN",
    flagEmoji: "🇻🇳",
    decimalPlaces: 0,
  },
  zar: {
    code: "zar",
    codeUpper: "ZAR",
    name: "South African Rand",
    symbol: "R",
    countryCode: "ZA",
    flagEmoji: "🇿🇦",
    decimalPlaces: 2,
  },
  xdr: {
    code: "xdr",
    codeUpper: "XDR",
    name: "IMF Special Drawing Rights",
    symbol: "SDR",
    countryCode: "XX",
    flagEmoji: "🌐",
    decimalPlaces: 6,
  },
};

/**
 * Map ISO 3166-1 alpha-2 country codes to currency codes
 * Used for auto-detecting user's currency based on their location
 */
export const COUNTRY_TO_CURRENCY: Record<string, string> = {
  US: "usd",
  AE: "aed",
  AR: "ars",
  AU: "aud",
  BD: "bdt",
  BH: "bhd",
  BM: "bmd",
  BR: "brl",
  CA: "cad",
  CH: "chf",
  CL: "clp",
  CN: "cny",
  CZ: "czk",
  DK: "dkk",
  // Eurozone countries
  AT: "eur", // Austria
  BE: "eur", // Belgium
  CY: "eur", // Cyprus
  EE: "eur", // Estonia
  FI: "eur", // Finland
  FR: "eur", // France
  DE: "eur", // Germany
  GR: "eur", // Greece
  IE: "eur", // Ireland
  IT: "eur", // Italy
  LV: "eur", // Latvia
  LT: "eur", // Lithuania
  LU: "eur", // Luxembourg
  MT: "eur", // Malta
  NL: "eur", // Netherlands
  PT: "eur", // Portugal
  SK: "eur", // Slovakia
  SI: "eur", // Slovenia
  ES: "eur", // Spain
  GB: "gbp",
  GE: "gel",
  HK: "hkd",
  HU: "huf",
  ID: "idr",
  IL: "ils",
  IN: "inr",
  JP: "jpy",
  KR: "krw",
  KW: "kwd",
  LK: "lkr",
  MM: "mmk",
  MX: "mxn",
  MY: "myr",
  NG: "ngn",
  NO: "nok",
  NZ: "nzd",
  PH: "php",
  PK: "pkr",
  PL: "pln",
  RU: "rub",
  SA: "sar",
  SE: "sek",
  SG: "sgd",
  TH: "thb",
  TR: "try",
  TW: "twd",
  UA: "uah",
  VE: "vef",
  VN: "vnd",
  ZA: "zar",
};

/**
 * Default currency when user's country cannot be determined
 */
export const DEFAULT_CURRENCY = "usd";

/**
 * List of all supported currency codes (lowercase)
 */
export const SUPPORTED_CURRENCY_CODES = Object.keys(SUPPORTED_CURRENCIES);

/**
 * Helper function to get currency metadata by code (case-insensitive)
 */
export function getCurrencyMetadata(currencyCode: string): CurrencyMetadata | undefined {
  return SUPPORTED_CURRENCIES[currencyCode.toLowerCase()];
}

/**
 * Helper function to get currency from country code
 */
export function getCurrencyFromCountry(countryCode: string): string {
  return COUNTRY_TO_CURRENCY[countryCode.toUpperCase()] || DEFAULT_CURRENCY;
}

/**
 * Generate flag emoji from ISO 3166-1 alpha-2 country code
 * @example getFlagEmoji("US") => "🇺🇸"
 */
export function getFlagEmoji(countryCode: string): string {
  const codePoints = [...countryCode.toUpperCase()].map((char) => 0x1f1e6 - 65 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}
